<template>
  <div class="outreach-contact-socials">
    <a
      v-for="(social, socialIndex) in contactSocials"
      :key="`social_${socialIndex}`"
      :href="social.url"
      rel="noopener noreferrer"
      target="_blank"
    >
      <BaseIcon
        v-if="social.newIcon"
        class="outreach-contact-socials-icon"
        :icon="social.icon"
      />
      <QIcon
        v-else
        :class="social.iconClass"
        :name="social.icon"
        size="20px"
      />
      <BaseStreemTooltip>
        {{ social.tooltip || social.url }}
      </BaseStreemTooltip>
    </a>
  </div>
</template>

<script>
import { getLocaleText } from "shared/boot/i18n";
import { BaseIcon, BaseStreemTooltip } from "shared/components/base";
import { SOCIAL_ICONS } from "shared/constants";
import { toSocialIconKey } from "shared/helpers/social";

export default {
  name: "OutreachContactSocials",
  components: {
    BaseIcon,
    BaseStreemTooltip,
  },
  props: {
    socials: {
      type: [Array, null],
      default: () => [],
    },
    emails: {
      type: [Array, null],
      default: () => [],
    },
    homepages: {
      type: [Array, null],
      default: () => [],
    },
    phones: {
      type: [Array, null],
      default: () => [],
    },
    invalidEmail: {
      type: Boolean,
    },
  },
  computed: {
    defaultSocials() {
      return (this.socials || []).filter((social) => social.defaultFlag);
    },
    defaultEmails() {
      return (this.emails || []).filter((email) => email.defaultFlag);
    },
    defaultHomepages() {
      return (this.homepages || []).filter((homepage) => homepage.defaultFlag);
    },
    defaultPhones() {
      return (this.phones || []).filter((phone) => phone.defaultFlag);
    },
    contactSocials() {
      const links = [];

      if (this.defaultEmails.length) {
        const emailDetails = this.invalidEmail
          ? {
              icon: "email",
              url: getLocaleText(
                "outreach_contact.undeliverable_email_tooltip"
              ),
              tooltip: getLocaleText(
                "outreach_contact.undeliverable_email_tooltip",
                { email: this.defaultEmails[0].email }
              ),
              invalidEmail: this.invalidEmail,
              iconClass: "undeliverable-email-badge",
            }
          : {
              icon: "email",
              url: `mailto:${this.defaultEmails[0].email}`,
              tooltip: this.defaultEmails[0].email,
            };

        links.push(emailDetails);
      }

      if (this.defaultPhones.length) {
        const phone = this.translatePhone(this.defaultPhones[0]);

        links.push({
          icon: "phone",
          newIcon: true,
          url: `tel:${phone}`,
          tooltip: phone,
        });
      }

      this.defaultSocials.forEach((social) => {
        let icon = "img:https://assets.streem.global/icons/earth.svg";

        const socialType = toSocialIconKey(social.socialType);

        if (SOCIAL_ICONS[socialType]) {
          icon = SOCIAL_ICONS[socialType];
        }

        links.push({
          icon,
          url: social.socialUrl,
        });
      });

      this.defaultHomepages.forEach((homepage) => {
        links.push({
          icon: "img:https://assets.streem.global/icons/earth.svg",
          url: homepage.homepage,
        });
      });

      return links;
    },
  },
  methods: {
    translatePhone(phone) {
      const lines = [];
      if (phone.countryCode) lines.push(`+${phone.countryCode}`);
      lines.push(phone.number);

      return lines.join(" ");
    },
  },
};
</script>

<style lang="scss" scoped>
.outreach-contact-socials {
  display: flex;
  gap: var(--s-spacing-md);
  align-items: center;

  &-icon {
    width: 20px;
    height: 20px;
    color: $ds-denim-9;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;

    .s-icon {
      font-size: 1em;
    }
  }
}

.undeliverable-email-badge::before {
  background-color: red;
  font-size: 10px;
  font-weight: bold;
  font-family: $typography-font-family;
  color: white;
  width: 12px;
  align-content: center;
  height: 12px;
  border-radius: 55%;
  position: absolute;
  top: -3px;
  right: -5px;
  content: "!";
}
</style>
